import { useMutation } from 'react-query';
import { authFetch } from '../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../CommonUtils/httpBodyOptions';
import { queryClient } from '../../../../config/queryClient';
import { BeneficiaryDTO, TrustGrantor, TrustType } from './useTrustsQuery';
import { queryKeys } from './queryKeys';
import { fetchPersonalInfo, PersonalInfoDTO } from './usePersonalInfoQuery';
import { useClientId } from '../../../_hooks/useClientId';
import {
  EstatePlanningPersonalInfoDTO,
  fetchEstatePlanningPersonalInfo,
} from './useEstatePlanningPersonalInfoQuery';
import { OptionType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';

export const UPSERT_TRUST_ENDPOINT = '/Consumer/TrustUpdate/Save';
export const UPSERT_TRUST_ESTATE_PLANNING_ENDPOINT = '/Consumer/EstatePlanningTrustUpdate/Save';

export interface UpsertTrustBody {
  Id: string;
  Name: string;
  TrustType: TrustType;
  Grantors: OptionType<TrustGrantor>[];
  IsGstExempt: boolean;
  Situs: string;
  Trustees: OptionType<number>[];
  DatedDate: string;
  Beneficiaries: {
    beneficiaries: BeneficiaryDTO[];
    lastBeneficiaryUpdate: string | null;
  };
  IncludeInGrossEstate: boolean | null;
  LiquidAssetDistributionStrategy: { PercentageAtFirstSpouseDeath: number } | null;
  IlliquidAssetDistributionStrategy: { AssetsAtFirstSpouseDeath: string[] } | null;
}

interface UpsertTrustParams {
  body: UpsertTrustBody;
}

const upsertTrust = (clientId: number, isEstatePlanning?: boolean) => async ({
  body,
}: UpsertTrustParams) => {
  return authFetch(
    isEstatePlanning ? UPSERT_TRUST_ESTATE_PLANNING_ENDPOINT : UPSERT_TRUST_ENDPOINT,
    {
      method: 'POST',
      ...httpBodyOptions({ clientId, ...body }, true),
    },
  );
};

interface UpsertTrustMutationParams {
  resetPersonalInfo(data: PersonalInfoDTO): void;
  isEstatePlanning?: boolean;
}

export const useUpsertTrustMutation = ({
  resetPersonalInfo,
  isEstatePlanning,
}: UpsertTrustMutationParams) => {
  const clientId = useClientId();

  const trustsQK = queryKeys.trusts(clientId);
  const personalInfoQK = queryKeys.personalInfo(clientId);
  const estatePlanningPersonalInfoQK = queryKeys.estatePlanningPersonalInfo(clientId);

  return useMutation(upsertTrust(clientId, isEstatePlanning), {
    onSuccess: async () => {
      await queryClient.refetchQueries(trustsQK);
      if (isEstatePlanning) {
        await queryClient.invalidateQueries(estatePlanningPersonalInfoQK);

        const estatePlanningInfoData = await queryClient.fetchQuery<EstatePlanningPersonalInfoDTO>(
          estatePlanningPersonalInfoQK,
          () => fetchEstatePlanningPersonalInfo({ clientId }),
        );

        resetPersonalInfo(estatePlanningInfoData.PersonalInfo);
      } else {
        await queryClient.invalidateQueries(personalInfoQK);

        const personalInfoData = await queryClient.fetchQuery<PersonalInfoDTO>(personalInfoQK, () =>
          fetchPersonalInfo({ clientId }),
        );

        resetPersonalInfo(personalInfoData);
      }
    },
  });
};
